import React, { useState } from 'react'
import { 
  useGetHashtagsQuery, 
  useCreateHashtagsMutation 
} from '../../graphql/generated/graphql'
import { 
  Box, 
  Typography, 
  Button, 
  Chip,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Container,
  alpha
} from '@mui/material'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import RefreshIcon from '@mui/icons-material/Refresh'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import { useNavigate } from 'react-router-dom'

export default function Trends() {
  const { data, loading, refetch } = useGetHashtagsQuery()
  const [createHashtags, { loading: generating }] = useCreateHashtagsMutation()
  const [activeTab, setActiveTab] = useState<'all' | 'youtube' | 'instagram' | 'tiktok' | 'facebook' | 'reddit'>('all')
  const [error, setError] = useState<string | null>(null) 
  const navigate = useNavigate()

  const handleGenerateTrends = async () => {
    try {
      setError(null) // Clear any previous errors
      
      const result = await createHashtags({ variables: { update: false } })
      
      if (!result.data?.createHashtags) {
        setError('Failed to generate trends. Please try again.')
        return
      }
      
      await refetch()
      
      // Check if we actually got any trends back
      const newTrends = data?.getHashtags?.trends
      if (newTrends && 
          newTrends.youtube.length === 0 && 
          newTrends.instagram.length === 0 && 
          newTrends.tiktok.length === 0 &&
          newTrends.facebook.length === 0 &&
          newTrends.reddit.length === 0) {
        setError('No hashtags found in your campaigns. Try creating some campaigns with hashtags first!')
      }
    } catch (error: any) {
      console.error('Error generating trends:', error)
      
      // Provide user-friendly error messages
      if (error.message?.includes('No campaigns found')) {
        setError('No campaigns found. Create some campaigns first to generate trends!')
      } else if (error.message?.includes('No hits found')) {
        setError('No campaign data found in the last 30 days. Try running some campaigns first!')
      } else {
        setError('Failed to generate trends. Please try again or contact support if the issue persists.')
      }
    }
  }

  const handleRefreshTrends = async () => {
    try {
      setError(null)
      
      const result = await createHashtags({ variables: { update: true } })
      
      if (!result.data?.createHashtags) {
        setError('Failed to refresh trends. Please try again.')
        return
      }
      
      await refetch()
    } catch (error: any) {
      console.error('Error refreshing trends:', error)
      setError('Failed to refresh trends. Please try again.')
    }
  }

  const handleCreateCampaign = (hashtag: string, platform: string) => {
    navigate('/new-campaign', {
      state: { 
        prefillHashtag: hashtag,
        prefillPlatform: platform 
      }
    })
  }

  if (loading) {
    return (
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '400px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        }}
      >
        <CircularProgress sx={{ color: 'white' }} size={60} />
      </Box>
    )
  }

  const trends = data?.getHashtags?.trends

  // First-time experience
  if (!trends || (
    trends.youtube.length === 0 && 
    trends.instagram.length === 0 && 
    trends.tiktok.length === 0 &&
    trends.facebook.length === 0 &&  
    trends.reddit.length === 0        
  )) {
    return (
      <Box 
        sx={{ 
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '60px 20px',
        }}
      >
        <Container maxWidth="md">
          <Box
            sx={{
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              padding: '60px 40px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 120,
                height: 120,
                margin: '0 auto 24px',
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.05) 100%)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              <TrendingUpIcon sx={{ fontSize: 60, color: 'white' }} />
            </Box>

            <Typography 
              variant="h3" 
              sx={{ 
                mb: 2, 
                color: 'white',
                fontWeight: 700,
                textShadow: '0 2px 10px rgba(0,0,0,0.1)',
              }}
            >
              Campaign Ideation Hub
            </Typography>

            <Typography 
              variant="h6" 
              sx={{ 
                mb: 4, 
                color: 'rgba(255, 255, 255, 0.9)',
                fontWeight: 400,
                maxWidth: '500px',
                margin: '0 auto 40px',
              }}
            >
              Discover trending hashtags from your campaigns and create new campaigns with one click.
            </Typography>

            <Box
              sx={{
                background: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                borderRadius: '16px',
                padding: '24px',
                mb: 4,
                textAlign: 'left',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  color: 'white', 
                  fontWeight: 600, 
                  mb: 2,
                }}
              >
                ✨ How it works:
              </Typography>
              <Box component="ul" sx={{ color: 'rgba(255, 255, 255, 0.9)', margin: 0, paddingLeft: '20px' }}>
                <li style={{ marginBottom: '8px' }}>We analyze hashtags from all your campaigns</li>
                <li style={{ marginBottom: '8px' }}>Calculate volume, growth trends, and engagement rates</li>
                <li>Show you what&apos;s actually working</li>
              </Box>
            </Box>

            {error && (
            <Alert 
              severity="error" 
              onClose={() => setError(null)}
              sx={{ 
                mb: 3,
                background: 'rgba(244, 67, 54, 0.15)',
                backdropFilter: 'blur(10px)',
                color: 'white',
                border: '1px solid rgba(244, 67, 54, 0.3)',
                borderRadius: '12px',
                '& .MuiAlert-icon': {
                  color: '#ef5350',
                },
              }}
            >
              {error}
            </Alert>
          )}
            <Button
              variant="contained"
              size="large"
              onClick={() => { void handleGenerateTrends() }}
              disabled={generating}
              startIcon={generating ? <CircularProgress size={20} /> : <RocketLaunchIcon />}
              sx={{
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                padding: '16px 48px',
                fontSize: '18px',
                fontWeight: 600,
                borderRadius: '12px',
                textTransform: 'none',
                boxShadow: '0 4px 20px rgba(245, 87, 108, 0.4)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
                  boxShadow: '0 6px 25px rgba(245, 87, 108, 0.5)',
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'rgba(255, 255, 255, 0.5)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {generating ? 'Analyzing Your Campaigns...' : 'Generate Trends'}
            </Button>
          </Box>
        </Container>
      </Box>
    )
  }

  // Get hashtags for active tab
  const getHashtagsForTab = () => {
    if (!trends) return []
    
    if (activeTab === 'all') {
      return [
        ...(trends.youtube || []).map(h => ({ ...h, platform: 'youtube' as const })),
        ...(trends.instagram || []).map(h => ({ ...h, platform: 'instagram' as const })),
        ...(trends.tiktok || []).map(h => ({ ...h, platform: 'tiktok' as const })),
        ...(trends.facebook || []).map(h => ({ ...h, platform: 'facebook' as const })),  // ADD
        ...(trends.reddit || []).map(h => ({ ...h, platform: 'reddit' as const })),      // ADD
      ]
        .filter(h => h.metrics)
        .sort((a, b) => (b.metrics?.volume ?? 0) - (a.metrics?.volume ?? 0))
        .slice(0, 20)
    }
    
    return (trends[activeTab] || []).map(h => ({ ...h, platform: activeTab }))
  }

  const hashtags = getHashtagsForTab()

  const getPlatformColor = (platform: string) => {
    switch (platform) {
      case 'youtube':
        return 'linear-gradient(135deg, #FF0000 0%, #CC0000 100%)'
      case 'instagram':
        return 'linear-gradient(135deg, #833ab4 0%, #fd1d1d 50%, #fcb045 100%)'
      case 'tiktok':
        return 'linear-gradient(135deg, #00f2ea 0%, #ff0050 100%)'
      case 'facebook': 
        return 'linear-gradient(135deg, #1877f2 0%, #0c5fc9 100%)'
      case 'reddit':   
        return 'linear-gradient(135deg, #ff4500 0%, #cc3700 100%)'
      default:
        return 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }
  }

  const getPlatformEmoji = (platform: string) => {
    switch (platform) {
      case 'youtube': return '📺'
      case 'instagram': return '📷'
      case 'tiktok': return '🎵'
      case 'facebook': return '👥'  
      case 'reddit': return '🤖'      
      default: return '🌐'
    }
  }

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '40px 20px',
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            mb: 4,
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box>
            <Typography 
              variant="h3" 
              sx={{ 
                mb: 1, 
                color: 'white',
                fontWeight: 700,
                textShadow: '0 2px 10px rgba(0,0,0,0.1)',
              }}
            >
              Campaign Ideation Hub
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)',
              }}
            >
              Trending hashtags from your campaigns
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => { void handleRefreshTrends() }}
            disabled={generating}
            startIcon={generating ? <CircularProgress size={20} /> : <RefreshIcon />}
            sx={{
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              color: 'white',
              padding: '12px 32px',
              fontSize: '16px',
              fontWeight: 600,
              borderRadius: '12px',
              textTransform: 'none',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.3)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
              },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.1)',
                color: 'rgba(255, 255, 255, 0.5)',
              },
              transition: 'all 0.3s ease',
            }}
          >
            {generating ? 'Refreshing...' : 'Refresh Trends'}
          </Button>
        </Box>
            {/* ADD THIS ERROR ALERT */}
            {error && (
              <Alert 
                severity="warning" 
                onClose={() => setError(null)}
                sx={{ 
                  mb: 3,
                  background: 'rgba(255, 152, 0, 0.15)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  border: '1px solid rgba(255, 152, 0, 0.3)',
                  borderRadius: '12px',
                  '& .MuiAlert-icon': {
                    color: '#ffb74d',
                  },
                  '& .MuiAlert-message': {
                    fontSize: '16px',
                  },
                }}
              >
                {error}
              </Alert>
            )}
        {/* Platform tabs */}
        <Box 
          sx={{ 
            mb: 4,
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(20px)',
            borderRadius: '16px',
            padding: '8px',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            display: 'inline-flex',
            gap: 1,
          }}
        >
          {[
            { id: 'all', label: 'All Platforms', emoji: '🌐' },
            { id: 'youtube', label: 'YouTube', emoji: '📺' },
            { id: 'instagram', label: 'Instagram', emoji: '📷' },
            { id: 'tiktok', label: 'TikTok', emoji: '🎵' },
            { id: 'facebook', label: 'Facebook', emoji: '👥' },  
            { id: 'reddit', label: 'Reddit', emoji: '🤖' },        
          ].map((tab) => (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              sx={{
                px: 3,
                py: 1.5,
                borderRadius: '12px',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                background: activeTab === tab.id 
                  ? 'rgba(255, 255, 255, 0.25)' 
                  : 'transparent',
                backdropFilter: activeTab === tab.id ? 'blur(10px)' : 'none',
                border: activeTab === tab.id 
                  ? '1px solid rgba(255, 255, 255, 0.3)' 
                  : '1px solid transparent',
                '&:hover': {
                  background: 'rgba(255, 255, 255, 0.2)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              {tab.emoji} {tab.label}
            </Button>
          ))}
        </Box>

          {/* Hashtag grid */}
          {hashtags.length === 0 ? (
            <Box
              sx={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(20px)',
                borderRadius: '16px',
                padding: '32px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
              }}
            >
              <Typography sx={{ color: 'white', textAlign: 'center' }}>
                No trending hashtags found for this platform. Try creating some campaigns first!
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {hashtags.map((item: any, index: number) => {
                const { tag, metrics, platform } = item
                
                // Skip invalid items
                if (!tag || !metrics) return null
                
                const platformEmoji = getPlatformEmoji(platform)
                
                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={`${String(platform)}-${String(tag)}-${index}`}>
                    <Card 
                      sx={{ 
                        height: '100%',
                        background: 'rgba(255, 255, 255, 0.1)',
                        backdropFilter: 'blur(20px)',
                        borderRadius: '20px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 12px 40px 0 rgba(31, 38, 135, 0.5)',
                          border: '1px solid rgba(255, 255, 255, 0.4)',
                        }
                      }}
                    >
                      <CardContent sx={{ padding: '24px' }}>
                        {/* Hashtag and platform badge */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                          <Typography 
                            variant="h5" 
                            component="div" 
                            sx={{ 
                              fontWeight: 700,
                              color: 'white',
                              wordBreak: 'break-word',
                            }}
                          >
                            {tag}
                          </Typography>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '12px',
                              background: getPlatformColor(platform),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '20px',
                              flexShrink: 0,
                              ml: 1,
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                            }}
                          >
                            {platformEmoji}
                          </Box>
                        </Box>
                        
                        {/* Metrics */}
                        <Box sx={{ mb: 3 }}>
                          {/* Volume */}
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              mb: 1.5,
                              padding: '12px',
                              background: 'rgba(255, 255, 255, 0.08)',
                              borderRadius: '12px',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                            }}
                          >
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              📊 Volume
                            </Typography>
                            <Typography variant="body2" fontWeight={700} sx={{ color: 'white' }}>
                              {metrics.volume.toLocaleString()}
                            </Typography>
                          </Box>
                          
                          {/* Growth */}
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              mb: 1.5,
                              padding: '12px',
                              background: metrics.growth > 0 
                                ? 'rgba(76, 175, 80, 0.15)' 
                                : 'rgba(244, 67, 54, 0.15)',
                              borderRadius: '12px',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                            }}
                          >
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              📈 Growth
                            </Typography>
                            <Typography 
                              variant="body2" 
                              fontWeight={700}
                              sx={{ 
                                color: metrics.growth > 0 ? '#4caf50' : '#f44336',
                              }}
                            >
                              {metrics.growth > 0 ? '+' : ''}{metrics.growth.toFixed(1)}%
                            </Typography>
                          </Box>
                          
                          {/* Engagement */}
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between',
                              padding: '12px',
                              background: 'rgba(255, 255, 255, 0.08)',
                              borderRadius: '12px',
                              border: '1px solid rgba(255, 255, 255, 0.1)',
                            }}
                          >
                            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                              ❤️ Engagement
                            </Typography>
                            <Typography variant="body2" fontWeight={700} sx={{ color: 'white' }}>
                              {metrics.engagement?.toFixed(1) || '0.0'}%
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* Create campaign button */}
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={() => { void handleCreateCampaign(tag, platform) }}
                          startIcon={<RocketLaunchIcon />}
                          sx={{
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            color: 'white',
                            padding: '12px',
                            fontSize: '14px',
                            fontWeight: 600,
                            borderRadius: '12px',
                            textTransform: 'none',
                            border: '1px solid rgba(255, 255, 255, 0.3)',
                            boxShadow: '0 4px 15px rgba(245, 87, 108, 0.3)',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
                              transform: 'translateY(-2px)',
                              boxShadow: '0 6px 20px rgba(245, 87, 108, 0.4)',
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          Create Campaign
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                )
              })}
            </Grid>
          )}
      </Container>
    </Box>
  )
}